import { iOSConnectSuccessResults, ConnectParams } from '@/types/ble'
import {
  BleDeviceTypes,
  bleDeviceNames,
  DEFAULT_ECG_TERM_HR,
  DEFAULT_CONSTANT_ECG
} from '@/constants/ble'
import { EventEmitter } from 'events'
import { BleProviderEventTypes } from './constants'
import { BLEProvider } from './types'
import { isEnv } from '@/utils'
import { Ble } from './messenger'
import store from '@/store'
import { BLEConfig } from '@/types/appSettings'

const IS_PRODUCTION = isEnv('production')

export const logger = (...args: any): any => {
  if (!IS_PRODUCTION) {
    console.log(...args)
  }
  return args
}

const event = new EventEmitter()

const BleProvider: BLEProvider = {
  ble: Ble,
  get events() {
    return event
  },
  devices: {},

  init() {
    try {
      // this.ble = Ble
      // this.events = new EventEmitter()
      this.devices = {}

      if (!this.isSupport()) {
        console.error('BLE connection not supported')
        return
      }

      this.addListener(
        BleProviderEventTypes.ADD_DEVICE,
        async (deviceName, address, deviceType) => {
          const device = {
            deviceType,
            deviceName,
            address,
            connected: false,
            deviceDisplayName: bleDeviceNames[deviceType]
          }
          this.setDevice(device)
          logger('[BLE_PROVIDER_LOG] EMIT_DEVICE_ADDED: ', device)
          this.events.emit(BleProviderEventTypes.DEVICE_ADDED, device)
        }
      )
      logger('[BLE_PROVIDER_LOG] EMIT_INITIALIZE_SUCCESS: ')
      this.events.emit(BleProviderEventTypes.INITIALIZE_SUCCESS)
    } catch (error) {
      console.error(error)
      logger('[BLE_PROVIDER_LOG] EMIT_INITIALIZE_ERROR: ', error)
      this.events.emit(BleProviderEventTypes.INITIALIZE_ERROR, error)
    }
  },

  isSupport() {
    if (process.env.NODE_ENV === 'development') return true
    return window.parent != window
  },

  changeNC1BLEConfig(address, params) {
    logger('ECG_CONFIG in changeNC1BLEConfig: ', { ...params })
    return new Promise<void>((resolve, reject) => {
      this.ble.changeNC1BLEConfig(address, resolve, reject, params)
    })
  },

  startScan() {
    Object.keys(this.devices).forEach(
      address => (this.devices[address].connected = false)
    )
    this.ble.startScan(
      this.handleAddDevice.bind(this),
      error => {
        logger('[BLE_PROVIDER_LOG] EMIT_START_SCAN_ERROR: ', error)
        this.events.emit(BleProviderEventTypes.START_SCAN_ERROR, error)
      },
      { setupNo10: true }
    )
  },

  stopScan() {
    return new Promise((resolve, reject) => {
      this.ble.stopScan(resolve, reject)
    })
  },

  async connect(address, params = {}) {
    try {
      if (!this.devices[address]) {
        throw Error('Address is not found')
      }

      const device = this.devices[address]

      logger('[BLE_PROVIDER_LOG] start to subscribe received value: ', device)
      this.ble.addReceivedValueListener(
        address,
        this.handleReceiveValue.bind(this)
      )

      const connectParams: ConnectParams = { name: device.deviceName }

      if (device.deviceType === BleDeviceTypes.ECG_SERVICE) {
        const configSetting = store.getters['appSettings/bleConfig'](
          device.address
        )
        if (configSetting) {
          const { config } = configSetting as BLEConfig<
            BleDeviceTypes.ECG_SERVICE
          >
          connectParams.constantECG = config.constantECG
          connectParams.termHR = config.termHR
        } else {
          connectParams.constantECG = DEFAULT_CONSTANT_ECG
          connectParams.termHR = DEFAULT_ECG_TERM_HR
        }
        logger('ECG_CONFIG in connect method: ', { ...connectParams })
      }

      const result = await new Promise<iOSConnectSuccessResults | void>(
        (resolve, reject) => {
          this.ble.connect(
            address,
            resolve,
            reject,
            () => this.handleDisconnect(device, true),
            Object.assign(connectParams, params)
          )
        }
      )

      this.devices[address].connected = true

      logger('[BLE_PROVIDER_LOG] EMIT_CONNECT: ', this.devices[address])
      this.events.emit(BleProviderEventTypes.CONNECT, this.devices[address])

      return result
    } catch (error) {
      logger('[BLE_PROVIDER_LOG] ERROR_CONNECT: ', error)
      throw error
    }
  },

  async close(address: string) {
    try {
      await new Promise((resolve, reject) => {
        this.ble.close(address, resolve, reject)
      })
      this.devices[address].connected = false // TODO: close時には不要？
      const device = this.devices[address]
      this.handleClose(device)
    } catch (error) {
      logger('[BLE_PROVIDER_LOG] ERROR_CLOSE: ', error)
      throw error
    }
  },

  handleClose(device, fromRemoteDevice = false) {
    logger('[BLE_PROVIDER_LOG] EMIT_CLOSE ', device, fromRemoteDevice)
    this.events.emit(BleProviderEventTypes.CLOSE, device, fromRemoteDevice)
  },

  async disconnect(address: string) {
    try {
      await new Promise((resolve, reject) => {
        this.ble.disconnect(address, resolve, reject)
      })
      this.devices[address].connected = false
      const device = this.devices[address]
      this.handleDisconnect(device)
    } catch (error) {
      logger('[BLE_PROVIDER_LOG] ERROR_DISCONNECT: ', error)
      throw error
    }
  },

  handleDisconnect(device, fromRemoteDevice = false) {
    logger('[BLE_PROVIDER_LOG] EMIT_DISCONNECT: ', device, fromRemoteDevice)
    this.events.emit(BleProviderEventTypes.DISCONNECT, device, fromRemoteDevice)
  },

  handleAddDevice(deviceName, address) {
    const deviceType = this.getDeviceType(deviceName)

    if (deviceType === null) {
      return
    }

    logger(
      '[BLE_PROVIDER_LOG] EMIT_ADD_DEVICE: ',
      deviceName,
      address,
      deviceType
    )
    this.events.emit(
      BleProviderEventTypes.ADD_DEVICE,
      deviceName,
      address,
      deviceType
    )
  },

  handleReceiveValue(readVal) {
    logger('[BLE_PROVIDER_LOG] EMIT_RECEIVE_VALUE: ')
    logger('==============================================')
    Object.keys(readVal).forEach(key =>
      logger(`${key}: `, (readVal as any)[key])
    )
    logger('==============================================')

    if (readVal.invalid) {
      return
    }

    this.events.emit(BleProviderEventTypes.RECEIVE_VALUE, readVal)

    switch (readVal.deviceType) {
      case BleDeviceTypes.ELECTRONIC_BLOOD_PRESSURE_MONITOR:
        this.events.emit(
          BleProviderEventTypes.RECEIVE_ELECTRONIC_BLOOD_PRESSURE_MONITOR,
          readVal
        )
        break
      case BleDeviceTypes.MEDICAL_BLOOD_PRESSURE_MONITOR:
        this.events.emit(
          BleProviderEventTypes.RECEIVE_MEDICAL_BLOOD_PRESSURE_MONITOR,
          readVal
        )
        break
      case BleDeviceTypes.ELECTRONIC_THERMOMETER:
        this.events.emit(
          BleProviderEventTypes.RECEIVE_ELECTRONIC_THERMOMETER,
          readVal
        )
        break
      case BleDeviceTypes.BLOOD_SUGAR_LEVEL_MEASUREMENT:
        this.events.emit(
          BleProviderEventTypes.RECEIVE_BLOOD_SUGAR_LEVEL_MEASUREMENT,
          readVal
        )
        break
      case BleDeviceTypes.SPO2_MONITOR_MIGHTY_SAT:
        this.events.emit(
          BleProviderEventTypes.RECEIVE_SPO2_MONITOR_MIGHTY_SAT,
          readVal
        )
        break
      case BleDeviceTypes.ECG_SERVICE:
        this.events.emit(BleProviderEventTypes.RECEIVE_ECG_SERVICE, readVal)
        break
      case BleDeviceTypes.SPO2_MONITOR_TM1121:
        this.events.emit(
          BleProviderEventTypes.RECEIVE_SPO2_MONITOR_TM1121,
          readVal
        )
        break
      case BleDeviceTypes.NONCONTACT_THERMOMETER:
        this.events.emit(
          BleProviderEventTypes.RECEIVE_NONCONTACT_THERMOMETER,
          readVal
        )
        break
      case BleDeviceTypes.BODY_COMPOSITION_MONITOR:
        this.events.emit(
          BleProviderEventTypes.RECEIVE_BODY_COMPOSITION_MONITOR,
          readVal
        )
        break
    }
  },

  getDeviceType(name) {
    switch (true) {
      case this.ble.isNumber1Device(name):
        return BleDeviceTypes.ELECTRONIC_BLOOD_PRESSURE_MONITOR
      case this.ble.isNumber2Device(name):
        return BleDeviceTypes.MEDICAL_BLOOD_PRESSURE_MONITOR
      case this.ble.isNumber3Device(name):
        return BleDeviceTypes.ELECTRONIC_THERMOMETER
      case this.ble.isNumber4Device(name):
        return BleDeviceTypes.BLOOD_SUGAR_LEVEL_MEASUREMENT
      case this.ble.isNumber5Device(name):
        return BleDeviceTypes.SPO2_MONITOR_MIGHTY_SAT
      case this.ble.isNumber6Device(name):
        return BleDeviceTypes.ECG_SERVICE
      case this.ble.isNumber7Device(name):
        return BleDeviceTypes.SPO2_MONITOR_TM1121
      case this.ble.isNumber9Device(name):
        return BleDeviceTypes.NONCONTACT_THERMOMETER
      case this.ble.isNumber10Device(name):
        return BleDeviceTypes.BODY_COMPOSITION_MONITOR
      default:
        return null
    }
  },

  setDevice(deviceInfo) {
    this.devices[deviceInfo.address] = deviceInfo
  },

  getDevice(address) {
    return this.devices[address] || null
  },

  getDeviceByDeviceType(deviceType) {
    const address = Object.keys(this.devices).find(
      address => this.devices[address].deviceType === deviceType
    )
    if (!address) {
      return null
    }
    return this.devices[address]
  },

  isConnected(address) {
    const device = this.getDevice(address)

    return device ? device.connected : false
  },

  addListener(eventType, listener) {
    this.events.on(eventType, listener)

    return () => {
      this.events.off(eventType, listener)
    }
  }
}

export default BleProvider
